import { useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useAppContext } from "@/providers";
import { t } from "@/lib/i18n/messages";
import { cn } from "@/lib/utils";
import type { Notification } from "@/types/notification";

interface NotificationsPanelProps {
  notifications: Notification[];
  loading: boolean;
  onMarkRead: (id: string) => Promise<void>;
  onMarkAllRead: () => Promise<void>;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
}

function formatTime(dateStr: string, locale: string): string {
  try {
    const date = new Date(dateStr);
    return date.toLocaleString(locale === "ar" ? "ar-SA" : "en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

export function NotificationsPanel({
  notifications,
  loading,
  onMarkRead,
  onMarkAllRead,
  onClose,
  anchorRef,
}: NotificationsPanelProps) {
  const { locale } = useAppContext();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeBtnRef.current?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "Tab" && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0] ?? null;
        const last = focusable[focusable.length - 1] ?? null;
        if (e.shiftKey) {
          if (document.activeElement === first && last) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last && first) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    },
    [onClose]
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose, anchorRef, handleKeyDown]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const panel = (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={t(locale, "notificationsTitle")}
      className={cn(
        "fixed top-16 end-4 z-50 w-80 max-h-[70vh] flex flex-col",
        "bg-card border border-border rounded-lg shadow-xl overflow-hidden",
        "animate-slide-in-top"
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h2 className="font-semibold text-foreground text-sm">
          {t(locale, "notificationsTitle")}
          {unreadCount > 0 && (
            <span className="ms-2 bg-error text-white text-xs rounded-pill px-1.5 py-0.5">
              {unreadCount}
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={() => void onMarkAllRead()}
              className="text-xs text-secondary hover:underline font-medium"
            >
              {t(locale, "markAllRead")}
            </button>
          )}
          <button
            ref={closeBtnRef}
            onClick={onClose}
            aria-label={t(locale, "close")}
            className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors duration-base"
          >
            <span aria-hidden="true" className="material-icons-outlined text-base">
              close
            </span>
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="overflow-y-auto flex-1 custom-scrollbar">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center px-4">
            <span className="material-icons-outlined text-3xl text-muted-foreground mb-2">
              notifications_none
            </span>
            <p className="text-sm text-foreground-secondary">
              {t(locale, "noNotifications")}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {t(locale, "noNotificationsDesc")}
            </p>
          </div>
        ) : (
          <ul role="list" className="divide-y divide-border">
            {notifications.map((n) => (
              <li
                key={n.id}
                className={cn(
                  "px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors duration-base",
                  !n.is_read && "bg-primary/5"
                )}
                onClick={() => {
                  if (!n.is_read) void onMarkRead(n.id);
                }}
              >
                <div className="flex items-start gap-2">
                  {!n.is_read && (
                    <span className="mt-1.5 flex-shrink-0 w-2 h-2 rounded-full bg-primary" />
                  )}
                  <div className={!n.is_read ? "" : "ms-4"}>
                    <p className="text-sm text-foreground leading-snug">
                      {n.message}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatTime(n.created_at, locale)}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}
